import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { formatNumber, formatCurrency } from '@/lib/utils';
import {
  STATUS_LABELS as SHARED_STATUS_LABELS,
  LABOUR_TYPE_LABELS as SHARED_LABOUR_TYPE_LABELS,
  SKILL_LEVEL_LABELS as SHARED_SKILL_LEVEL_LABELS,
} from '@shared/dashboard-contract';

export const CHART_COLORS = ['#3b5998', '#1e3a5f', '#818cf8', '#a5b4fc', '#c7d2fe', '#60a5fa'];

// Central label mappings live in the shared dashboard contract so the worker
// and web can never drift on status / classification rendering.
export const STATUS_LABELS = SHARED_STATUS_LABELS;
export const LABOUR_TYPE_LABELS = SHARED_LABOUR_TYPE_LABELS;
export const SKILL_LEVEL_LABELS = SHARED_SKILL_LEVEL_LABELS;

// Guard against non-finite values from the API so cards/charts never render
// "₹NaN" or "NaN" when a number column is missing or malformed.
export function safeNum(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function compactINR(value: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(safeNum(value));
}

// Bound the chart height to the number of series so a chart with one or two
// values does not render as an oversized empty canvas.
export function adaptiveChartHeight(count: number, min = 180, max = 320): number {
  return Math.max(min, Math.min(max, 60 + count * 24));
}

export interface StatCardDialogLink {
  label: string;
  to: string;
  value?: number;
}

export interface StatCardDialog {
  title: string;
  description?: string;
  links: StatCardDialogLink[];
}

export interface StatCardDef {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  to?: string;
  accent: 'amber' | 'red' | 'brand' | 'blue' | 'emerald';
  /** Combined cards open a popover with per-entity drill-downs instead of a link. */
  dialog?: StatCardDialog;
}

export const COLOR_CLASSES: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-500',
  emerald: 'bg-emerald-50 text-emerald-500',
  amber: 'bg-amber-50 text-amber-500',
  red: 'bg-red-50 text-red-500',
  brand: 'bg-brand-50 text-brand-500',
};

function StatCardDialogPopup({ card }: { card: StatCardDef }) {
  const [open, setOpen] = useState(false);
  const dialog = card.dialog!;
  const inner = (
    <div className="flex items-center gap-3 h-full">
      <div className={`p-2 rounded-lg ${COLOR_CLASSES[card.accent]}`}>{card.icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-secondary line-clamp-2">{card.label}</p>
        <p className="text-xl font-semibold text-primary truncate">{card.value}</p>
      </div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label={`${card.label}: ${card.value}`}
          className="block w-full h-full rounded-lg transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <Card className="h-full"><CardContent className="p-4 h-full">{inner}</CardContent></Card>
        </button>
      </DialogTrigger>
      <DialogContent aria-label={`${card.label} breakdown`}>
        <DialogHeader>
          <DialogTitle>{dialog.title}</DialogTitle>
          {dialog.description ? <DialogDescription>{dialog.description}</DialogDescription> : null}
        </DialogHeader>
        <div className="grid gap-2">
          {dialog.links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              aria-label={`${link.label}: ${link.value !== undefined ? formatNumber(link.value) : ''}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 p-3 hover:bg-surface-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <span className="text-sm font-medium text-primary">{link.label}</span>
              <span className="text-sm font-semibold text-primary">{link.value !== undefined ? formatNumber(link.value) : ''}</span>
            </Link>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StatCard({ card }: { card: StatCardDef }) {
  const inner = (
    <div className="flex items-center gap-3 h-full">
      <div className={`p-2 rounded-lg ${COLOR_CLASSES[card.accent]}`}>{card.icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-secondary line-clamp-2">{card.label}</p>
        <p className="text-xl font-semibold text-primary truncate">{card.value}</p>
      </div>
    </div>
  );
  if (card.to) {
    return (
      <Link
        to={card.to}
        aria-label={`${card.label}: ${card.value}`}
        className="block h-full rounded-lg transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <Card className="h-full"><CardContent className="p-4 h-full">{inner}</CardContent></Card>
      </Link>
    );
  }
  if (card.dialog) {
    return <StatCardDialogPopup card={card} />;
  }
  return (
    <Card className="h-full"><CardContent className="p-4 h-full">{inner}</CardContent></Card>
  );
}

export function StatGrid({ cards }: { cards: StatCardDef[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {cards.map((card, i) => <StatCard key={i} card={card} />)}
    </div>
  );
}

/**
 * Six-column stat grid for the Site Engineer six-card dashboard. One card per
 * row on small phones, two per row on compact/tablet, three on large tablets,
 * and all six in a balanced single row on wide desktop. No horizontal scroll.
 */
export function StatGridSix({ cards }: { cards: StatCardDef[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map((card, i) => <StatCard key={i} card={card} />)}
    </div>
  );
}

/**
 * Four-column stat grid for the Manager / Cluster Head four-card dashboards.
 * One card per row on small phones, two per row on compact/tablet, and all
 * four in a balanced single row on wide desktop. No horizontal scroll.
 */
export function StatGridFour({ cards }: { cards: StatCardDef[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card, i) => <StatCard key={i} card={card} />)}
    </div>
  );
}

export function SectionEmpty({ message }: { message: string }) {
  return <p className="text-muted text-sm text-center py-10">{message}</p>;
}

export function SectionHeader({ title, link }: { title: string; link?: { to: string; label: string } }) {
  return (
    <CardHeader className="flex flex-row items-center justify-between gap-2">
      <CardTitle className="text-base">{title}</CardTitle>
      {link && (
        <Link to={link.to} aria-label={link.label}>
          <Button variant="ghost" size="sm" className="gap-1 text-brand-500">
            {link.label} <ArrowRight size={14} />
          </Button>
        </Link>
      )}
    </CardHeader>
  );
}

export function ClassificationCallout({ admin }: { admin: boolean }) {
  return (
    <p className="text-xs text-muted mt-1 text-center">
      Some labour is unclassified.&nbsp;
      {admin
        ? <Link className="text-brand-500 underline" to="/admin/categories">Classify it under Labour Categories.</Link>
        : 'Ask an administrator to classify it under Labour Categories.'}
    </p>
  );
}

export function totalINR(value: unknown): string {
  return formatCurrency(safeNum(value));
}

export function headcountNum(value: unknown): string {
  return formatNumber(safeNum(value));
}
